import React, { useState, useContext } from "react";

import { Button, CssBaseline, TextField, Typography, InputAdornment, IconButton, CircularProgress } from "@material-ui/core";
import { Visibility, VisibilityOff, MailOutline, LockOutlined } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { MARCA } from "../../context/DarkMode";
import logoBranco from "../../assets/allarmi-logo-branco.webp";
import simbolo from "../../assets/allarmi-simbolo.png";

const useStyles = makeStyles((theme) => ({
  tela: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)",
    background: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  marca: {
    position: "relative",
    overflow: "hidden",
    background: MARCA.escuro,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "48px 56px",
    [theme.breakpoints.down("sm")]: { padding: "24px 20px", gap: 8 },
  },
  simbolo: {
    position: "absolute",
    right: -120,
    bottom: -120,
    width: 420,
    opacity: 0.12,
    pointerEvents: "none",
    [theme.breakpoints.down("sm")]: { width: 200, right: -60, bottom: -70 },
  },
  logo: { height: 44, alignSelf: "flex-start", [theme.breakpoints.down("sm")]: { height: 32 } },
  chamada: { maxWidth: 440, position: "relative", [theme.breakpoints.down("sm")]: { display: "none" } },
  titulo: { fontSize: 34, fontWeight: 700, lineHeight: 1.15, marginBottom: 16 },
  destaque: { color: MARCA.limao },
  lista: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10, color: "rgba(255,255,255,.8)" },
  ponto: { display: "inline-block", width: 8, height: 8, borderRadius: 2, background: MARCA.limao, marginRight: 10 },
  rodapeMarca: { fontSize: 12, color: "rgba(255,255,255,.55)", position: "relative", [theme.breakpoints.down("sm")]: { display: "none" } },
  lado: { display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 20px" },
  cartao: {
    width: "100%",
    maxWidth: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    padding: "36px 32px",
    boxShadow: "0 12px 40px rgba(1, 27, 1, .10)",
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: { padding: "28px 20px" },
  },
  entrar: { fontSize: 24, fontWeight: 700, marginBottom: 4 },
  sub: { color: theme.palette.text.secondary, marginBottom: 20, fontSize: 14 },
  botao: { marginTop: 20, height: 48, fontSize: 16, borderRadius: 10 },
  ajuda: { marginTop: 18, fontSize: 12.5, color: theme.palette.text.secondary, textAlign: "center" },
}));

const Login = () => {
  const c = useStyles();
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [entrando, setEntrando] = useState(false);
  const { handleLogin } = useContext(AuthContext);

  const mudar = (e) => setUser({ ...user, [e.target.name]: e.target.value });

  const enviar = async (e) => {
    e.preventDefault();
    setEntrando(true);
    try {
      await handleLogin(user);
    } finally {
      setEntrando(false);
    }
  };

  return (
    <div className={c.tela}>
      <CssBaseline />
      <section className={c.marca}>
        <img src={simbolo} alt="" className={c.simbolo} />
        <img src={logoBranco} alt="Allarmi Tecnologia e Monitoramento" className={c.logo} />
        <div className={c.chamada}>
          <div className={c.titulo}>
            Central de Suporte <span className={c.destaque}>Allarmi</span>
          </div>
          <ul className={c.lista}>
            <li><span className={c.ponto} />Atendimento aos técnicos pelo WhatsApp</li>
            <li><span className={c.ponto} />Chamados, formulários e protocolos num só lugar</li>
            <li><span className={c.ponto} />Indicadores da operação em tempo real</li>
          </ul>
        </div>
        <div className={c.rodapeMarca}>Allarmi · Tecnologia e Monitoramento</div>
      </section>

      <main className={c.lado}>
        <form className={c.cartao} noValidate onSubmit={enviar}>
          <Typography component="h1" className={c.entrar}>Entrar</Typography>
          <Typography className={c.sub}>Use o e-mail e a senha do seu usuário.</Typography>
          <TextField
            variant="outlined" margin="normal" required fullWidth autoFocus
            id="email" name="email" autoComplete="email" label={i18n.t("login.form.email")}
            value={user.email} onChange={mudar}
            InputProps={{ startAdornment: <InputAdornment position="start"><MailOutline color="action" /></InputAdornment> }}
          />
          <TextField
            variant="outlined" margin="normal" required fullWidth
            id="password" name="password" autoComplete="current-password" label={i18n.t("login.form.password")}
            type={showPassword ? "text" : "password"} value={user.password} onChange={mudar}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockOutlined color="action" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword((v) => !v)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" fullWidth variant="contained" color="primary" className={c.botao} disabled={entrando}>
            {entrando ? <CircularProgress size={22} color="inherit" /> : i18n.t("login.buttons.submit")}
          </Button>
          <div className={c.ajuda}>Esqueceu a senha? Fale com o administrador do sistema.</div>
        </form>
      </main>
    </div>
  );
};

export default Login;
